import React, { useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import Select, { SingleValue } from "react-select";
import { RootDispatch, RootState } from "../../store";
import {
  getSingleEpcQuoteThunk,
  confirmEpcOrderThunk,
} from "../../store/slices/epcSlice";
import toast from "react-hot-toast";
import {
  Trash2,
  Plus,
  XCircle,
  Loader2,
  Edit2,
  CheckCircle,
  ChevronDown,
  ChevronRight,
  CalendarDays,
  PackageOpen,
} from "lucide-react";
import {
  getUnitsByType,
  getDefaultUnitForType,
} from "../../utils/warehouse.util";

interface AddEpcOrderModalProps {
  isOpen: boolean;
  onClose: () => void;
  quotationId: string;
  onSubmit: (orderData: any, createNewOrder?: boolean) => Promise<void>;
  isLoading?: boolean;
  address?: string;
  canCreateOrder: boolean;
}

interface ItemOption {
  value: string;
  label: string;
  type: "product" | "material";
  data: any;
}

interface IBookedItem {
  _id: string;
  productName: string;
  price: number;
  quantity: number;
  unit: string;
  discount: number;
  type: "product" | "material";
  unitType?: string;
}

const COST_OPTIONS = [
  "Installation cost",
  "Geda-Fee",
  "Net-Meter Charges",
  "File Preparation Charge(Stamp Paper)",
  "Commision",
];

const getOrderCreatedTime = (order: any) => {
  const timestamp = Date.parse(order.createdDate || order.createdAt || "");
  return Number.isNaN(timestamp) ? 0 : timestamp;
};

const formatOrderStatus = (status?: string) =>
  (status || "Status unavailable")
    .replace(/[-_]/g, " ")
    .replace(/\b\w/g, (character) => character.toUpperCase());

const getOrderStatusClasses = (status?: string) => {
  const normalizedStatus = (status || "").toLowerCase();
  if (/approved|finalized|delivered|complete/.test(normalizedStatus)) {
    return "bg-emerald-50 text-emerald-700 ring-emerald-600/20";
  }
  if (/cancel|reject|fail/.test(normalizedStatus)) {
    return "bg-red-50 text-red-700 ring-red-600/20";
  }
  if (/pending|draft|created/.test(normalizedStatus)) {
    return "bg-amber-50 text-amber-700 ring-amber-600/20";
  }
  return "bg-slate-100 text-slate-700 ring-slate-500/20";
};

const mapOrderItems = (goods: any[], isExtra: boolean): IBookedItem[] =>
  goods
    .filter((item) => !!item.isExtra === isExtra)
    .map((item) => ({
      _id: item._id,
      productName: item.productName || "Item name",
      price: item.price || 0,
      quantity: item.quantity || 0,
      unit: item.unit || "-",
      discount: item.discount || 0,
      type: item.type,
      unitType: item.unitType,
    }));

const AddEpcOrderModal: React.FC<AddEpcOrderModalProps> = ({
  isOpen,
  onClose,
  quotationId,
  onSubmit,
  isLoading = false,
  address,
  canCreateOrder,
}) => {
  const dispatch: RootDispatch = useDispatch();
  const boms = useSelector((state: RootState) => state.billOfMaterials.boms);
  const { products } = useSelector((state: RootState) => state.product);
  const { rawMaterials } = useSelector((state: RootState) => state.storeInward);
  const role = useSelector((state: RootState) => state.auth.currentUser?.role);
  const [bookedProducts, setBookedProducts] = useState<IBookedItem[]>([]);
  const [extraProducts, setExtraProducts] = useState<IBookedItem[]>([]);
  const [transportPrice, setTransportPrice] = useState<number>(0);
  const [discountPrice, setDiscountPrice] = useState<number>(0);
  const [selectedBom, setSelectedBom] = useState<SingleValue<ItemOption>>(null);
  const [selectedOption, setSelectedOption] =
    useState<SingleValue<ItemOption>>(null);
  const [manualQty, setManualQty] = useState<number>(1);
  const [manualUnit, setManualUnit] = useState<string>("");

  const [extraCosts, setExtraCosts] = useState<
    { type: string; amount: number }[]
  >([]);
  const [selectedExtraType, setSelectedExtraType] = useState<string>("");
  const [isFetching, setIsFetching] = useState(false);

  const [isReadOnly, setIsReadOnly] = useState(false);
  const [isFinalized, setIsFinalized] = useState(false);
  const [orders, setOrders] = useState<any[]>([]);
  const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null);
  const [latestOrderId, setLatestOrderId] = useState<string | null>(null);
  const [isOrderHistoryExpanded, setIsOrderHistoryExpanded] = useState(false);
  const [confirmedDate, setConfirmedDate] = useState<string | null>(null);
  const [createdDate, setCreatedDate] = useState<string | null>(null);
  const [orderAddress, setOrderAddress] = useState<string>(address || "");
  const [deductFromCommission, setDeductFromCommission] = useState(false);
  const [isNewOrder, setIsNewOrder] = useState(false);
  const isEditMode = !!createdDate;

  useEffect(() => {
    const loadData = async () => {
      if (!isOpen || !quotationId) return;
      setIsFetching(true);
      try {
        const res = await dispatch(
          getSingleEpcQuoteThunk(quotationId),
        ).unwrap();

        const orderRecords = Array.isArray(res?.orders)
          ? res.orders.map((order: any, index: number) => ({
              ...order,
              modalKey: String(
                order._id || order.orderNumber || `order-${index}`,
              ),
            }))
          : [];
        const latestOrder = orderRecords.reduce(
          (latest: any, order: any) =>
            !latest || getOrderCreatedTime(order) > getOrderCreatedTime(latest)
              ? order
              : latest,
          null,
        );
        const orderData = latestOrder || res?.orderData;
        const goods = Array.isArray(orderData?.goods) ? orderData.goods : [];
        const hasCreatedDate = !!res?.orderCreatedDate || !!orderData;
        const hasConfirmedDate = !!res?.orderConfirmedDate;

        setOrders(orderRecords);
        setLatestOrderId(latestOrder?.modalKey || null);
        setSelectedOrderId(latestOrder?.modalKey || null);
        setIsOrderHistoryExpanded(false);

        if (hasCreatedDate && orderData) {
          setBookedProducts(mapOrderItems(goods, false));
          setExtraProducts(mapOrderItems(goods, true));
          setDeductFromCommission(orderData.deductFromCommission || false);
          setTransportPrice(orderData.transportPrice || 0);
          setDiscountPrice(orderData.discountPrice || 0);
          setExtraCosts(orderData.extraCosts || []);
          setOrderAddress(orderData.billingAddress?.line1 || address || "");
        } else {
          setDeductFromCommission(false);
          setBookedProducts([]);
          setExtraProducts([]);
          setTransportPrice(0);
          setDiscountPrice(0);
          setExtraCosts([]);
          setOrderAddress(address || "");
        }

        // Apply state logic based strictly on dates
        setIsReadOnly(hasCreatedDate || hasConfirmedDate);
        setIsFinalized(hasConfirmedDate);
        setConfirmedDate(res?.orderConfirmedDate || null);
        setCreatedDate(
          res?.orderCreatedDate ||
            orderData?.createdDate ||
            orderData?.createdAt ||
            null,
        );
      } catch (error) {
        console.error(error);
      } finally {
        setIsFetching(false);
      }
    };
    loadData();
  }, [isOpen, quotationId, dispatch, address]);

  const handleSelectOrder = (order: any) => {
    const goods = Array.isArray(order.goods) ? order.goods : [];
    setSelectedOrderId(order.modalKey);
    setBookedProducts(mapOrderItems(goods, false));
    setExtraProducts(mapOrderItems(goods, true));
    setDeductFromCommission(order.deductFromCommission || false);
    setTransportPrice(order.transportPrice || 0);
    setDiscountPrice(order.discountPrice || 0);
    setExtraCosts(order.extraCosts || []);
    setOrderAddress(order.billingAddress?.line1 || address || "");
    setCreatedDate(order.createdDate || order.createdAt || null);
    setConfirmedDate(null);
  };

  const isViewingPreviousOrder =
    orders.length > 1 &&
    !!selectedOrderId &&
    selectedOrderId !== latestOrderId;
  const sortedOrders = [...orders].sort(
    (first, second) =>
      getOrderCreatedTime(second) - getOrderCreatedTime(first),
  );
  const selectedOrder = orders.find(
    (order) => order.modalKey === selectedOrderId,
  );
  const getStatusForOrder = (order: any) =>
    order.orderStatus ||
    (order.modalKey === latestOrderId
      ? isFinalized
        ? "finalized"
        : "draft"
      : "status-unavailable");

  const itemOptions: ItemOption[] = useMemo(
    () => [
      ...products.map((p) => ({
        value: p._id,
        label: p.productName,
        type: "product" as const,
        data: p,
      })),
      ...rawMaterials.map((m: any) => ({
        value: m._id,
        label: m.name,
        type: "material" as const,
        data: m,
      })),
    ],
    [products, rawMaterials],
  );

  useEffect(() => {
    if (selectedOption?.type === "material") {
      setManualUnit(getDefaultUnitForType(selectedOption.data.unitType) || "");
    } else {
      setManualUnit(selectedOption ? "-" : "");
    }
  }, [selectedOption]);

  const blockNumberControls = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "ArrowUp" || e.key === "ArrowDown") e.preventDefault();
  };

  const blockScroll = (e: React.WheelEvent<HTMLInputElement>) => {
    (e.target as HTMLInputElement).blur();
  };

  const handleAddExtraCost = () => {
    if (!selectedExtraType) return toast.error("Please select a cost type");
    setExtraCosts((prev) => [...prev, { type: selectedExtraType, amount: 0 }]);
    setSelectedExtraType("");
  };

  const handleUpdateExtraCost = (index: number, amount: number) => {
    setExtraCosts((prev) =>
      prev.map((c, i) => (i === index ? { ...c, amount } : c)),
    );
  };

  const handleUpdateExtraProducts = (
    index: number,
    field: keyof IBookedItem,
    value: any,
  ) => {
    setExtraProducts((prev) =>
      prev.map((p, i) => (i === index ? { ...p, [field]: value } : p)),
    );
  };

  const handleConfirmOrder = async () => {
    try {
      await dispatch(confirmEpcOrderThunk(quotationId)).unwrap();
      handleCloseModal();
    } catch (error: any) {
      toast.error(error);
    }
  };
  const handleCloseModal = () => {
    setSelectedBom(null);
    setBookedProducts([]);
    setExtraProducts([]);
    setExtraCosts([]);
    setIsNewOrder(false);
    setOrders([]);
    setSelectedOrderId(null);
    setLatestOrderId(null);
    setIsOrderHistoryExpanded(false);
    onClose();
  };
  const handleCreateNewOrder = () => {
    setSelectedBom(null);
    setSelectedOption(null);
    setBookedProducts([]);
    setExtraProducts([]);
    setTransportPrice(0);
    setDiscountPrice(0);
    setExtraCosts([]);
    setSelectedExtraType("");
    setDeductFromCommission(false);
    setConfirmedDate(null);
    setCreatedDate(null);
    setIsFinalized(false);
    setIsReadOnly(false);
    setIsNewOrder(true);
    setSelectedOrderId(null);
    setIsOrderHistoryExpanded(false);
    setOrderAddress(address || "");
  };
  const handleLoadBOM = (selectedBom: SingleValue<ItemOption>) => {
    setSelectedBom(selectedBom!);
    const selectedBOM = boms.find((b: any) => b._id === selectedBom?.value);
    if (!selectedBOM) return;
    setBookedProducts([]);
    selectedBOM.items?.forEach((it: any) => {
      const id = it.product?._id || it.rawMaterial?._id || "";
      const name = it.product?.productName || it.rawMaterial?.name || "";
      if (id && !bookedProducts.find((p) => p._id === id)) {
        setBookedProducts((prev) => [
          ...prev,
          {
            _id: id,
            productName: name,
            price: it.product?.price || 0,
            quantity: it.quantity,
            unit: it.type === "product" ? "-" : it.displayUnit || "Unit",
            discount: 0,
            type: it.type as any,
            unitType: it.rawMaterial?.unitType,
          },
        ]);
      }
    });
    toast.success("BOM loaded");
  };

  const handleFinalSubmit = async () => {
    if (bookedProducts.length === 0) return toast.error("Please add items.");
    const totalRowDiscount = bookedProducts.reduce(
      (acc, p) => acc + (Number(p.discount) || 0),
      0,
    );
    if (
      totalRowDiscount > 0 &&
      Math.abs(totalRowDiscount - discountPrice) > 0.1
    ) {
      toast.error(
        `Discount Mismatch: Row sum is ₹${totalRowDiscount.toFixed(2)} but Overall is ₹${discountPrice.toFixed(2)}.`,
      );
      return;
    }
    await onSubmit(
      {
        quotationId,
        address: orderAddress,
        deductFromCommission,
        items: bookedProducts.map((item) => ({
          _id: item._id,
          type: item.type,
          quantity: item.quantity,
          unit: item.unit,
          price: item.price,
          discount: item.discount,
          unitType: item.unitType,
        })),
        extraItems: extraProducts.map((item) => ({
          _id: item._id,
          type: item.type,
          quantity: item.quantity,
          unit: item.unit,
          price: item.price,
          discount: item.discount,
          unitType: item.unitType,
        })),
        transportPrice,
        discountPrice,
        extraCosts,
      },
      isNewOrder,
    );
  };

  const formatOptionLabel = ({ label, type }: ItemOption) => (
    <div className="flex justify-between items-center w-full text-sm">
      <span>{label}</span>
      <span
        className={`text-[9px] px-1.5 py-0.5 rounded font-bold uppercase shrink-0 ${type === "product" ? "bg-blue-100 text-blue-700" : "bg-green-100 text-green-700"}`}
      >
        {type}
      </span>
    </div>
  );

  const customSelectStyles = {
    control: (base: any) => ({
      ...base,
      minHeight: "38px",
      fontSize: "0.875rem",
      borderColor: "#D1D5DB",
      borderRadius: "0.5rem",
    }),
    menuPortal: (base: any) => ({ ...base, zIndex: 9999 }),
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[9999] bg-black/50 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4">
      <style>{`input::-webkit-outer-spin-button, input::-webkit-inner-spin-button { -webkit-appearance: none; margin: 0; } input[type=number] { -moz-appearance: textfield; }`}</style>
      <div className="bg-white shadow-2xl flex flex-col overflow-hidden text-gray-800 w-full h-[100dvh] sm:h-[92vh] sm:max-w-[95vw] lg:w-[1100px] rounded-none sm:rounded-2xl">
        <div className="sticky top-0 z-20 flex items-start sm:items-center justify-between gap-3 px-4 py-3 sm:px-6 sm:py-5 border-b border-gray-100 bg-white">
          <div className="flex flex-col gap-1">
            <div className="flex items-center gap-4">
              <h2 className="text-base sm:text-xl font-bold leading-tight">
                {isFetching
                  ? "Loading..."
                  : orders.length > 1
                    ? "EPC Order Details"
                    : isFinalized
                      ? "Finalized EPC Order"
                      : isReadOnly
                        ? "Review EPC Order Draft"
                        : isEditMode
                          ? "Edit EPC Order Draft"
                          : "Create EPC Order Draft"}
              </h2>
              {isReadOnly &&
                !isFinalized &&
                !isViewingPreviousOrder &&
                !isFetching && (
                  <button
                    onClick={() => setIsReadOnly(false)}
                    className="flex items-center gap-1.5 px-3 py-1 bg-blue-50 text-blue-600 rounded-md border border-blue-100 hover:bg-blue-100 transition-colors font-bold text-xs"
                  >
                    <Edit2 size={12} />
                    Edit
                  </button>
                )}
            </div>
            <div className="flex flex-wrap items-center gap-2 mt-2">
              {orders.length <= 1 && createdDate && (
                <span className="flex items-center gap-1.5 px-3 py-1 bg-blue-50 text-blue-700 rounded-md border border-blue-200 text-xs font-bold shadow-sm">
                  Drafted:{" "}
                  {new Date(createdDate).toLocaleString("en-IN", {
                    hour12: true,
                  })}
                </span>
              )}
              {orders.length <= 1 && isFinalized && confirmedDate && (
                <span className="flex items-center gap-1.5 px-3 py-1 bg-green-50 text-green-700 rounded-md border border-green-200 text-xs font-bold shadow-sm">
                  <CheckCircle size={16} className="text-green-600" />
                  Confirmed:{" "}
                  {new Date(confirmedDate).toLocaleString("en-IN", {
                    hour12: true,
                  })}
                </span>
              )}
            </div>
            {orders.length > 1 && (
              <p className="mt-2 hidden text-xs text-gray-500 sm:block">
                Review the customer&apos;s order history and select an order to
                view its materials.
              </p>
            )}
          </div>
          <button
            onClick={handleCloseModal}
            className="text-gray-500 hover:text-gray-700 p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <XCircle size={22} />
          </button>
        </div>

        {isFetching ? (
          <div className="flex-1 flex flex-col items-center justify-center gap-3">
            <Loader2 className="animate-spin text-blue-600" size={40} />
            <span className="text-gray-500 font-medium">
              Fetching draft information...
            </span>
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto px-3 py-4 sm:p-6 space-y-4 sm:space-y-6 text-sm">
            {isReadOnly && orders.length > 1 && selectedOrder && (
              <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
                <div className="flex flex-col gap-4 border-b border-slate-200 bg-gradient-to-r from-slate-50 to-white p-4 sm:flex-row sm:items-center sm:justify-between sm:px-5">
                  <div className="flex min-w-0 items-center gap-3">
                    <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-blue-100 text-blue-700">
                      <PackageOpen size={20} />
                    </div>
                    <div className="min-w-0">
                      <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-slate-500">
                        Customer order history
                      </p>
                      <p className="mt-0.5 font-semibold text-slate-800">
                        {orders.length} EPC orders
                      </p>
                      <p className="mt-0.5 text-xs text-slate-500">
                        Choose an order to review its materials and details.
                      </p>
                    </div>
                  </div>
                  <button
                    type="button"
                    aria-expanded={isOrderHistoryExpanded}
                    onClick={() =>
                      setIsOrderHistoryExpanded((expanded) => !expanded)
                    }
                    className="inline-flex min-h-10 shrink-0 items-center justify-center gap-2 rounded-lg border border-slate-300 bg-white px-3.5 text-xs font-bold text-slate-700 shadow-sm transition hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700"
                  >
                    {isOrderHistoryExpanded ? "Hide orders" : "Browse orders"}
                    <ChevronDown
                      size={15}
                      className={`transition-transform ${
                        isOrderHistoryExpanded ? "rotate-180" : ""
                      }`}
                    />
                  </button>
                </div>

                <div className="flex flex-col gap-3 bg-blue-50/60 p-4 sm:flex-row sm:items-center sm:justify-between sm:px-5">
                  <div className="min-w-0">
                    <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-blue-700">
                      Currently viewing
                    </p>
                    <h3 className="mt-1 truncate text-base font-bold text-slate-900">
                      {selectedOrder.orderNumber || "EPC Order"}
                    </h3>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <span
                      className={`inline-flex items-center rounded-full px-2.5 py-1 text-[11px] font-bold ring-1 ring-inset ${getOrderStatusClasses(
                        getStatusForOrder(selectedOrder),
                      )}`}
                    >
                      {formatOrderStatus(getStatusForOrder(selectedOrder))}
                    </span>
                    {(selectedOrder.createdDate || selectedOrder.createdAt) && (
                      <span className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-2.5 py-1 text-[11px] font-medium text-slate-600">
                        <CalendarDays size={13} />
                        {new Date(
                          selectedOrder.createdDate || selectedOrder.createdAt,
                        ).toLocaleDateString("en-IN", {
                          day: "2-digit",
                          month: "short",
                          year: "numeric",
                        })}
                      </span>
                    )}
                    <span className="rounded-full border border-slate-200 bg-white px-2.5 py-1 text-[11px] font-medium text-slate-600">
                      {Array.isArray(selectedOrder.goods)
                        ? selectedOrder.goods.length
                        : bookedProducts.length + extraProducts.length}{" "}
                      items
                    </span>
                  </div>
                </div>

                {isOrderHistoryExpanded && (
                  <div className="max-h-72 overflow-y-auto border-t border-slate-200 bg-slate-50/70 p-3 sm:p-4">
                    <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
                      {sortedOrders.map((order, index) => {
                        const isSelected = order.modalKey === selectedOrderId;
                        const status = getStatusForOrder(order);
                        const orderDate = order.createdDate || order.createdAt;

                        return (
                          <button
                            key={order.modalKey}
                            type="button"
                            aria-pressed={isSelected}
                            onClick={() => handleSelectOrder(order)}
                            className={`group rounded-xl border p-3.5 text-left shadow-sm transition-all ${
                              isSelected
                                ? "border-blue-400 bg-white ring-2 ring-blue-100"
                                : "border-slate-200 bg-white hover:-translate-y-0.5 hover:border-blue-300 hover:shadow-md"
                            }`}
                          >
                            <div className="flex items-start justify-between gap-3">
                              <div className="flex min-w-0 items-center gap-2.5">
                                <span
                                  className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${
                                    isSelected
                                      ? "bg-blue-100 text-blue-700"
                                      : "bg-slate-100 text-slate-600"
                                  }`}
                                >
                                  <PackageOpen size={17} />
                                </span>
                                <span className="min-w-0">
                                  <span className="block truncate text-sm font-bold text-slate-900">
                                    {order.orderNumber || `Order ${index + 1}`}
                                  </span>
                                  {orderDate && (
                                    <span className="mt-0.5 block text-[11px] text-slate-500">
                                      {new Date(orderDate).toLocaleDateString(
                                        "en-IN",
                                        {
                                          day: "2-digit",
                                          month: "short",
                                          year: "numeric",
                                        },
                                      )}
                                    </span>
                                  )}
                                </span>
                              </div>
                              {isSelected && (
                                <CheckCircle
                                  size={17}
                                  className="shrink-0 text-blue-600"
                                />
                              )}
                            </div>
                            <div className="mt-3 flex items-center justify-between gap-2 border-t border-slate-100 pt-3">
                              <span
                                className={`inline-flex rounded-full px-2.5 py-1 text-[10px] font-bold ring-1 ring-inset ${getOrderStatusClasses(
                                  status,
                                )}`}
                              >
                                {formatOrderStatus(status)}
                              </span>
                              <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-slate-500 transition-colors group-hover:text-blue-700">
                                {Array.isArray(order.goods)
                                  ? order.goods.length
                                  : 0}{" "}
                                items
                                <ChevronRight size={14} />
                              </span>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </section>
            )}

            {!isReadOnly && (
              <div className="bg-white border border-gray-200 rounded-2xl p-4 sm:p-5 shadow-sm space-y-4">
                <div className="flex flex-col md:flex-row gap-4 md:items-end w-full">
                  <div className="flex-1 min-w-[250px]">
                    <label className="text-[11px] font-bold text-gray-400 uppercase tracking-wider mb-1 block">
                      Load BOM Template
                    </label>

                    <div className="flex gap-2 w-full">
                      <div className="w-full ">
                        <Select
                          value={selectedBom}
                          onChange={handleLoadBOM}
                          options={
                            boms.map((bom) => ({
                              value: bom._id,
                              label: bom.name,
                              type: "bom",
                              data: bom,
                            })) as unknown as ItemOption[]
                          }
                          formatOptionLabel={formatOptionLabel}
                          styles={customSelectStyles}
                          isClearable
                          menuPortalTarget={document.body}
                          placeholder="Search BOMs..."
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
            {(!isReadOnly || extraProducts.length > 0) && (
              <div className="border border-gray-200 rounded-xl overflow-hidden shadow-sm bg-red-50 p-6">
                {!isReadOnly && (
                  <div
                    className={`flex flex-col md:flex-row gap-2 items-end ${extraProducts.length > 0 && "pb-3"}`}
                  >
                    <div className="flex-1 min-w-[250px]">
                      <label className="text-[11px] font-bold text-gray-400 uppercase mb-1 block">
                        Add Extra Product / Material (Additional cost)
                      </label>
                      <Select
                        value={selectedOption}
                        onChange={setSelectedOption}
                        options={itemOptions}
                        formatOptionLabel={formatOptionLabel}
                        styles={customSelectStyles}
                        placeholder="Search items..."
                        isClearable
                        menuPortalTarget={document.body}
                      />
                    </div>
                    <div className="w-full sm:w-20">
                      <label className="text-[11px] font-bold text-gray-400 uppercase mb-1 block">
                        Qty
                      </label>
                      <input
                        type="number"
                        value={manualQty}
                        onKeyDown={blockNumberControls}
                        onWheel={blockScroll}
                        onChange={(e) =>
                          setManualQty(parseFloat(e.target.value) || 0)
                        }
                        className="w-full h-11 px-3 border border-gray-300 rounded-xl text-sm outline-none focus:ring-1 focus:ring-blue-500"
                      />
                    </div>
                    <div className="w-full sm:w-28">
                      <label className="text-[11px] font-bold text-gray-400 uppercase mb-1 block">
                        Unit
                      </label>
                      {selectedOption?.type === "material" &&
                      selectedOption.data.unitType ? (
                        <select
                          value={manualUnit}
                          onChange={(e) => setManualUnit(e.target.value)}
                          className="w-full h-11 px-3 border border-gray-300 rounded-xl text-sm bg-white outline-none focus:ring-1 focus:ring-blue-500"
                        >
                          <option value="">Select Unit</option>
                          {getUnitsByType(selectedOption.data.unitType).map(
                            (u) => (
                              <option key={u.value} value={u.value}>
                                {u.label}
                              </option>
                            ),
                          )}
                        </select>
                      ) : (
                        <div className="p-2 bg-gray-100 border border-gray-200 rounded-lg text-sm text-gray-500 text-center">
                          -
                        </div>
                      )}
                    </div>
                    <button
                      onClick={() => {
                        if (!selectedOption) return;
                        const { type, value, label, data } = selectedOption;
                        setExtraProducts((prev) => [
                          ...prev,
                          {
                            _id: value,
                            productName: label,
                            price: type === "product" ? data.price || 0 : 0,
                            quantity: manualQty,
                            unit: manualUnit,
                            discount: 0,
                            type,
                            unitType: data.unitType,
                          },
                        ]);
                        setSelectedOption(null);
                        setManualQty(1);
                      }}
                      className="h-[42px] w-full sm:w-auto px-4 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-semibold flex items-center justify-center transition-all shadow-sm"
                    >
                      <Plus size={16} />
                    </button>
                  </div>
                )}
                {extraProducts.length > 0 && (
                  <div className="flex items-center gap-4 mb-4 border border-gray-200 p-2 px-4 rounded-lg bg-white">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        disabled={isReadOnly}
                        type="checkbox"
                        checked={deductFromCommission}
                        onChange={(e) =>
                          setDeductFromCommission(e.target.checked)
                        }
                        className="w-4 h-4 accent-red-600 cursor-pointer disabled:opacity-100 disabled:cursor-not-allowed"
                      />
                      <span className="text-sm text-gray-700 font-medium">
                        Deduct from SalesRep's commission.
                      </span>
                    </label>
                  </div>
                )}
                {extraProducts.length > 0 && (
                  <div className="border border-gray-200 rounded-xl overflow-hidden shadow-sm bg-white">
                    {/* MOBILE CARDS */}
                    <div className="block md:hidden divide-y divide-gray-100">
                      {extraProducts.map((item, index) => (
                        <div key={index} className="p-4 space-y-3">
                          <div className="flex items-start justify-between gap-3">
                            <div>
                              <div className="font-semibold text-sm text-gray-800">
                                {item.productName}
                              </div>

                              <div
                                className={`text-[10px] font-bold mt-1 uppercase inline-flex px-2 py-0.5 rounded-full ${
                                  item.type === "product"
                                    ? "bg-blue-100 text-blue-700"
                                    : "bg-green-100 text-green-700"
                                }`}
                              >
                                {item.type}
                              </div>
                            </div>

                            {!isReadOnly && (
                              <button
                                onClick={() =>
                                  setExtraProducts((p) =>
                                    p.filter((_, i) => i !== index),
                                  )
                                }
                                className="text-gray-400 hover:text-red-500 p-1"
                              >
                                <Trash2 size={16} />
                              </button>
                            )}
                          </div>

                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <label className="text-[10px] font-bold text-gray-400 uppercase block mb-1">
                                Price
                              </label>

                              <input
                                type="number"
                                disabled={isReadOnly}
                                value={item.price}
                                onKeyDown={blockNumberControls}
                                onWheel={blockScroll}
                                onChange={(e) =>
                                  handleUpdateExtraProducts(
                                    index,
                                    "price",
                                    parseFloat(e.target.value) || 0,
                                  )
                                }
                                className="w-full h-10 px-2 border border-gray-200 rounded-lg outline-none focus:ring-1 focus:ring-blue-500 disabled:bg-gray-50"
                              />
                            </div>

                            <div>
                              <label className="text-[10px] font-bold text-gray-400 uppercase block mb-1">
                                Qty
                              </label>

                              <input
                                type="number"
                                disabled={isReadOnly}
                                value={item.quantity}
                                onKeyDown={blockNumberControls}
                                onWheel={blockScroll}
                                onChange={(e) =>
                                  handleUpdateExtraProducts(
                                    index,
                                    "quantity",
                                    parseFloat(e.target.value) || 0,
                                  )
                                }
                                className="w-full h-10 px-2 border border-gray-200 rounded-lg outline-none focus:ring-1 focus:ring-blue-500 disabled:bg-gray-50"
                              />
                            </div>

                            <div>
                              <label className="text-[10px] font-bold text-gray-400 uppercase block mb-1">
                                Unit
                              </label>

                              {item.type === "material" && item.unitType ? (
                                <select
                                  disabled={isReadOnly}
                                  value={item.unit}
                                  onChange={(e) =>
                                    handleUpdateExtraProducts(
                                      index,
                                      "unit",
                                      e.target.value,
                                    )
                                  }
                                  className="w-full h-10 px-2 border border-gray-200 rounded-lg outline-none bg-white disabled:bg-gray-50"
                                >
                                  {getUnitsByType(item.unitType).map((u) => (
                                    <option key={u.value} value={u.value}>
                                      {u.label}
                                    </option>
                                  ))}
                                </select>
                              ) : (
                                <div className="h-10 flex items-center justify-center border border-gray-200 rounded-lg bg-gray-50 text-gray-500">
                                  -
                                </div>
                              )}
                            </div>

                            <div>
                              <label className="text-[10px] font-bold text-gray-400 uppercase block mb-1">
                                Discount
                              </label>

                              <input
                                type="number"
                                disabled={isReadOnly}
                                value={item.discount || 0}
                                onKeyDown={blockNumberControls}
                                onWheel={blockScroll}
                                onChange={(e) =>
                                  handleUpdateExtraProducts(
                                    index,
                                    "discount",
                                    parseFloat(e.target.value) || 0,
                                  )
                                }
                                className="w-full h-10 px-2 border border-gray-200 rounded-lg outline-none focus:ring-1 focus:ring-blue-500 disabled:bg-gray-50"
                              />
                            </div>
                          </div>

                          <div className="flex items-center justify-between pt-1 border-t border-gray-100">
                            <span className="text-xs font-bold text-gray-500 uppercase">
                              Total
                            </span>

                            <span className="text-base font-bold text-gray-900">
                              ₹
                              {(
                                (item.price || 0) * (item.quantity || 0) -
                                (item.discount || 0)
                              ).toFixed(2)}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* DESKTOP TABLE */}
                    <div className="hidden md:block overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50 text-[11px] font-bold text-gray-500 uppercase">
                          <tr>
                            <th className="px-3 sm:px-5 py-2.5 text-left">
                              Additional Item Details
                            </th>
                            <th className="px-3 sm:px-5 py-2.5 text-left w-28">
                              Price (₹)
                            </th>
                            <th className="px-3 sm:px-5 py-2.5 text-left w-24">
                              Qty
                            </th>
                            <th className="px-3 sm:px-5 py-2.5 text-left w-28">
                              Unit
                            </th>
                            <th className="px-3 sm:px-5 py-2.5 text-left w-28">
                              Discount (₹)
                            </th>
                            <th className="px-3 sm:px-5 py-2.5 text-right w-32">
                              Total
                            </th>

                            {!isReadOnly && (
                              <th className="px-3 sm:px-5 py-2.5 w-12"></th>
                            )}
                          </tr>
                        </thead>

                        <tbody className="divide-y divide-gray-100 bg-white">
                          {extraProducts.map((item, index) => (
                            <tr
                              key={index}
                              className="hover:bg-gray-50 transition-colors text-sm"
                            >
                              <td className="px-3 sm:px-5 py-2.5 font-medium">
                                {item.productName}

                                <div
                                  className={`text-[9px] font-bold mt-1 uppercase ${
                                    item.type === "product"
                                      ? "text-blue-600"
                                      : "text-green-600"
                                  }`}
                                >
                                  {item.type}
                                </div>
                              </td>

                              <td className="px-3 sm:px-5 py-2.5">
                                <input
                                  type="number"
                                  disabled={isReadOnly}
                                  value={item.price}
                                  onKeyDown={blockNumberControls}
                                  onWheel={blockScroll}
                                  onChange={(e) =>
                                    handleUpdateExtraProducts(
                                      index,
                                      "price",
                                      parseFloat(e.target.value) || 0,
                                    )
                                  }
                                  className="w-full p-1 border border-gray-200 rounded outline-none focus:ring-1 focus:ring-blue-500 disabled:bg-gray-50"
                                />
                              </td>

                              <td className="px-3 sm:px-5 py-2.5">
                                <input
                                  type="number"
                                  disabled={isReadOnly}
                                  value={item.quantity}
                                  onKeyDown={blockNumberControls}
                                  onWheel={blockScroll}
                                  onChange={(e) =>
                                    handleUpdateExtraProducts(
                                      index,
                                      "quantity",
                                      parseFloat(e.target.value) || 0,
                                    )
                                  }
                                  className="w-full p-1 border border-gray-200 rounded outline-none focus:ring-1 focus:ring-blue-500 disabled:bg-gray-50"
                                />
                              </td>

                              <td className="px-3 sm:px-5 py-2.5">
                                {item.type === "material" && item.unitType ? (
                                  <select
                                    disabled={isReadOnly}
                                    value={item.unit}
                                    onChange={(e) =>
                                      handleUpdateExtraProducts(
                                        index,
                                        "unit",
                                        e.target.value,
                                      )
                                    }
                                    className="w-full p-1 border border-gray-200 rounded outline-none bg-white disabled:bg-gray-50"
                                  >
                                    {getUnitsByType(item.unitType).map((u) => (
                                      <option key={u.value} value={u.value}>
                                        {u.label}
                                      </option>
                                    ))}
                                  </select>
                                ) : (
                                  <div className="text-center text-gray-500">
                                    -
                                  </div>
                                )}
                              </td>

                              <td className="px-3 sm:px-5 py-2.5">
                                <input
                                  type="number"
                                  disabled={isReadOnly}
                                  value={item.discount || 0}
                                  onKeyDown={blockNumberControls}
                                  onWheel={blockScroll}
                                  onChange={(e) =>
                                    handleUpdateExtraProducts(
                                      index,
                                      "discount",
                                      parseFloat(e.target.value) || 0,
                                    )
                                  }
                                  className="w-full p-1 border border-gray-200 rounded outline-none focus:ring-1 focus:ring-blue-500 disabled:bg-gray-50"
                                />
                              </td>

                              <td className="px-3 sm:px-5 py-2.5 text-right font-bold text-gray-900">
                                ₹
                                {(
                                  (item.price || 0) * (item.quantity || 0) -
                                  (item.discount || 0)
                                ).toFixed(2)}
                              </td>

                              {!isReadOnly && (
                                <td className="px-3 sm:px-5 py-2.5 text-right">
                                  <button
                                    onClick={() =>
                                      setExtraProducts((p) =>
                                        p.filter((_, i) => i !== index),
                                      )
                                    }
                                    className="text-gray-400 hover:text-red-500 p-1 transition-colors"
                                  >
                                    <Trash2 size={16} />
                                  </button>
                                </td>
                              )}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            )}
            {bookedProducts.length > 0 && (
              <div className="border border-gray-200 rounded-xl overflow-hidden shadow-sm bg-white">
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50 text-[11px] font-bold text-gray-500 uppercase">
                      <tr>
                        <th className="px-3 sm:px-5 py-2.5 text-left">
                          Item Details {selectedBom?.label}
                        </th>
                        <th className="px-3 sm:px-5 py-2.5 text-left w-24">
                          Qty
                        </th>
                        <th className="px-3 sm:px-5 py-2.5 text-left w-28">
                          Unit
                        </th>
                        {!isReadOnly && (
                          <th className="px-3 sm:px-5 py-2.5 w-12"></th>
                        )}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 bg-white">
                      {bookedProducts.map((item, index) => (
                        <tr
                          key={index}
                          className="hover:bg-gray-50 transition-colors text-sm"
                        >
                          <td className="px-3 sm:px-5 py-2.5 font-medium">
                            {item.productName}
                            <div
                              className={`text-[9px] font-bold mt-1 uppercase ${item.type === "product" ? "text-blue-600" : "text-green-600"}`}
                            >
                              {item.type}
                            </div>
                          </td>

                          <td className="px-3 sm:px-5 py-2.5 font-medium">
                            {item.quantity}
                          </td>

                          <td className="px-3 sm:px-5 py-2.5 font-medium">
                            {item.unit}
                          </td>

                          {!isReadOnly && (
                            <td className="px-3 sm:px-5 py-2.5 text-right">
                              <button
                                onClick={() =>
                                  setBookedProducts((p) =>
                                    p.filter((_, i) => i !== index),
                                  )
                                }
                                className="text-gray-400 hover:text-red-500 p-1 transition-colors"
                              >
                                <Trash2 size={16} />
                              </button>
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            <div className="pt-2 border-t border-gray-100 space-y-2">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6"></div>
              <div className="w-full">
                <label className="text-[11px] font-bold text-gray-400 uppercase tracking-wider block">
                  Installation / Billing Address
                </label>
                <textarea
                  disabled={isReadOnly}
                  value={orderAddress}
                  onChange={(e) => setOrderAddress(e.target.value)}
                  rows={2}
                  className="w-full p-2.5 border border-gray-300 rounded-lg outline-none focus:ring-1 focus:ring-blue-500 text-sm disabled:bg-gray-50 resize-none"
                  placeholder="Enter full address..."
                />
              </div>
              {!isReadOnly && (
                <div className="space-y-2 max-w-md">
                  <label className="text-[11px] font-bold text-blue-600 uppercase tracking-wider block">
                    Add Additional Cost Fields
                  </label>
                  <div className="flex gap-2">
                    <select
                      value={selectedExtraType}
                      onChange={(e) => setSelectedExtraType(e.target.value)}
                      className="flex-1 h-11 px-3 border border-gray-300 rounded-xl text-sm bg-white outline-none focus:ring-1 focus:ring-blue-500"
                    >
                      <option value="">Select Cost Type...</option>
                      {COST_OPTIONS.filter(
                        (opt) => !extraCosts.some((c) => c.type === opt),
                      ).map((opt) => (
                        <option key={opt} value={opt}>
                          {opt}
                        </option>
                      ))}
                    </select>
                    <button
                      onClick={handleAddExtraCost}
                      disabled={!selectedExtraType}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-bold h-[38px] transition-all flex items-center gap-2 disabled:opacity-50"
                    >
                      <Plus size={16} /> Add Field
                    </button>
                  </div>
                </div>
              )}

              {extraCosts.length > 0 && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6 bg-gray-50/50 p-6 rounded-xl border border-gray-100">
                  {extraCosts.map((cost, idx) => (
                    <div key={idx} className="space-y-1.5 relative group">
                      <div className="flex justify-between items-center">
                        <label className="text-[11px] font-bold text-gray-500 uppercase tracking-wide truncate">
                          {cost.type}
                        </label>
                        {!isReadOnly && (
                          <button
                            onClick={() =>
                              setExtraCosts((prev) =>
                                prev.filter((_, i) => i !== idx),
                              )
                            }
                            className="text-red-500 hover:text-red-800 transition-colors p-1"
                            title="Remove field"
                          >
                            <Trash2 size={14} />
                          </button>
                        )}
                      </div>
                      <input
                        type="number"
                        disabled={isReadOnly}
                        value={cost.amount || ""}
                        onKeyDown={blockNumberControls}
                        onWheel={blockScroll}
                        onChange={(e) =>
                          handleUpdateExtraCost(
                            idx,
                            parseFloat(e.target.value) || 0,
                          )
                        }
                        className="w-full p-2.5 border border-gray-300 rounded-lg outline-none focus:ring-1 focus:ring-blue-500 bg-white text-sm font-semibold shadow-sm disabled:bg-gray-50"
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-gray-100 bg-gray-50 px-4 py-4 sm:px-6">
          <button
            onClick={handleCloseModal}
            className="rounded-lg border border-gray-300 px-5 py-2 text-sm font-bold text-gray-700 shadow-sm transition-all hover:bg-gray-100"
          >
            Cancel
          </button>
          {isReadOnly ? (
            <>
              {!isFinalized && !isViewingPreviousOrder && (
                <button
                  onClick={handleConfirmOrder}
                  disabled={isLoading || (!canCreateOrder && role !== "admin")}
                  className="rounded-lg bg-green-600 px-6 py-2 text-sm font-bold text-white shadow-md transition-all hover:bg-green-700 disabled:opacity-50 sm:px-8"
                >
                  Confirm & Place Order
                </button>
              )}
              {isFinalized && (
                <button
                  onClick={handleCreateNewOrder}
                  className="rounded-lg border border-blue-200 bg-blue-50 px-5 py-2 text-sm font-bold text-blue-700 shadow-sm transition-all hover:bg-blue-100"
                >
                  Create New Order
                </button>
              )}
            </>
          ) : (
            <>
              <button
                onClick={handleFinalSubmit}
                disabled={isLoading || bookedProducts.length === 0}
                className={`px-8 py-2 rounded-lg font-bold text-sm shadow-md transition-all ${isEditMode ? "bg-blue-600 hover:bg-blue-700" : "bg-green-600 hover:bg-green-700"} text-white disabled:opacity-50`}
              >
                {isLoading
                  ? "Processing..."
                  : isEditMode
                    ? "Update Draft"
                    : "Create Draft Order"}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AddEpcOrderModal;
